import React, { useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Stack,
} from '@mui/material';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import GroupsIcon from '@mui/icons-material/Groups';
import FavoriteIcon from '@mui/icons-material/Favorite';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

// Background images that rotate on refresh
const heroBackgrounds = [
  '/dallas1.png',
  '/dallas2.png',
  '/dallas3.png',
  '/country1.png',
];

const values = [
  {
    icon: VolunteerActivismIcon,
    title: 'Service',
    description: 'We believe in selfless service to our community, helping those in need without expecting anything in return.',
  },
  {
    icon: GroupsIcon,
    title: 'Community',
    description: 'We are stronger together. Our volunteers come from all walks of life, united by a common goal.',
  },
  {
    icon: FavoriteIcon,
    title: 'Compassion',
    description: 'Every motorist we help is a neighbor. We treat everyone with kindness, respect, and understanding.',
  },
  {
    icon: EmojiEventsIcon,
    title: 'Excellence',
    description: 'We strive to provide the best assistance possible, continuously improving our skills and response times.',
  },
];

function About() {
  // Random background image on each render
  const heroBackground = useMemo(() => {
    return heroBackgrounds[Math.floor(Math.random() * heroBackgrounds.length)];
  }, []);

  return (
    <Box>
      {/* Hero */}
      <Box
        sx={{
          pt: 16,
          pb: 12,
          position: 'relative',
          overflow: 'hidden',
          color: '#fff',
        }}
      >
        {/* Background Image */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url("${heroBackground}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            zIndex: 0,
          }}
        />
        {/* Dark Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(26, 35, 126, 0.88) 0%, rgba(13, 27, 94, 0.92) 100%)',
            zIndex: 1,
          }}
        />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ maxWidth: 700 }}>
            <Typography
              variant="overline"
              sx={{ color: 'secondary.main', fontWeight: 700, letterSpacing: 2 }}
            >
              About Us
            </Typography>
            <Typography variant="h1" sx={{ mt: 1, mb: 3, fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
              About the Rangers
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400, lineHeight: 1.7 }}>
              Texas Highway Rangers is a nonprofit built on the dedication of volunteers 
              and the generosity of our community. Every mile we cover is powered by 
              donated time, resources, and heart.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Mission */}
      <Box sx={{ py: 12, bgcolor: '#fff' }}>
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  height: 400,
                  borderRadius: 4,
                  background: 'linear-gradient(135deg, #1a237e 0%, #534bae 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    backgroundImage: 'url("/about-hero.jpg")',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    opacity: 0.3,
                  }}
                />
                <Typography
                  variant="h2"
                  sx={{
                    color: '#fff',
                    textAlign: 'center',
                    position: 'relative',
                    zIndex: 1,
                    px: 4,
                  }}
                >
                  Serving North Texas Since 2024
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography
                variant="overline"
                sx={{ color: 'secondary.main', fontWeight: 700, letterSpacing: 2 }}
              >
                Our Mission
              </Typography>
              <Typography variant="h2" sx={{ mt: 1, mb: 3 }}>
                No Texan Left Stranded
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                As Texas Highway Rangers, our dedication to service drives everything we do. 
                Founded with a passion for helping others, we provide essential roadside 
                assistance across North Texas, including jump starts, tire changes, and fuel delivery.
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Serving the Dallas area, we are proud to be a volunteer-driven organization 
                sustained entirely through generous community donations. Our mission is simple: 
                to lend a helping hand and ensure no Texan is left stranded.
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Every volunteer on our team gives their time freely, motivated only by the 
                desire to help our fellow Texans. We don't charge for our services, and we 
                never will. That's the Ranger way.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Values */}
      <Box sx={{ py: 12, bgcolor: '#fafafa' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="overline"
              sx={{ color: 'secondary.main', fontWeight: 700, letterSpacing: 2 }}
            >
              What Drives Us
            </Typography>
            <Typography variant="h2" sx={{ mt: 1 }}>
              Our Core Values
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {values.map((value, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    textAlign: 'center',
                    p: 2,
                    border: '1px solid',
                    borderColor: 'grey.200',
                    boxShadow: 'none',
                    transition: 'all 0.3s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: '0 8px 30px rgba(26, 35, 126, 0.1)',
                    },
                  }}
                >
                  <CardContent>
                    <Avatar
                      sx={{
                        width: 64,
                        height: 64,
                        bgcolor: 'primary.main',
                        mx: 'auto',
                        mb: 3,
                      }}
                    >
                      <value.icon sx={{ fontSize: 32 }} />
                    </Avatar>
                    <Typography variant="h4" sx={{ mb: 2 }}>
                      {value.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {value.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Team/Volunteer CTA */}
      <Box sx={{ py: 12, bgcolor: '#fff' }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h2" sx={{ mb: 3 }}>
              Join Our Team
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}
            >
              Interested in becoming a Highway Ranger? We're always looking for dedicated 
              volunteers who share our passion for helping others. No experience necessary – 
              just a willingness to serve.
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="center"
            >
              <Typography
                variant="body1"
                sx={{
                  bgcolor: 'grey.100',
                  px: 4,
                  py: 2,
                  borderRadius: 2,
                  fontWeight: 500,
                }}
              >
                📧 howdy@highwayrangers.com
              </Typography>
            </Stack>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

export default About;
